import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import useDimensions from '../../hooks/useDimensions'
import useProviders from '../../hooks/useProviders'
import { useBoundStore } from '../../store/store'
import classNames from '../../utils/classNames'
import ProvidersSlider from './providers-slider'

interface ProvidersProps {
  category: string
  selectedPlatform: string
  setSelectedPlatform: (platform: string) => void
  moreProviders: () => void
}

const Providers = ({
  category,
  selectedPlatform,
  setSelectedPlatform,
  moreProviders,
}: ProvidersProps) => {
  const { isDesktop } = useDimensions()
  const categories = useBoundStore(state => state.casino.categories)
  const { providers } = useProviders()
  const theme = useBoundStore(state => state.common.theme)
  const { t } = useTranslation('casino')

  const selectedCategory = useMemo(
    () => categories.find(c => c.id === category),
    [categories, category],
  )

  const enableSlider = useMemo(() => {
    if (!categories) return false
    return isDesktop
      ? Object.keys(selectedCategory?.providers ?? {}).length > 6
      : Object.keys(selectedCategory?.providers ?? {}).length > 3
  }, [categories, category])

  const renderProvider = useCallback(
    (provider: string) => {
      const isActive = selectedPlatform === provider

      if (!providers?.[provider]) return null
      return (
        <button
          className={classNames(
            'px-1 py-0 xl:px-1.5 xl:py-0',
            enableSlider &&
              'flex-[0_0_calc(100%_/_3)] lg:flex-[0_0_calc(100%_/_6)]',
          )}
          key={provider}
          aria-label={provider}
          onClick={() => {
            document
              .getElementById('games-container')
              ?.scrollIntoView({ behavior: 'smooth' })
            isActive ? setSelectedPlatform('') : setSelectedPlatform(provider)
          }}>
          <div
            className={classNames(
              'border-2 overflow-hidden rounded-md xl:rounded-xl',
              isActive ? 'border-highlight' : 'border-transparent',
            )}>
            <div className="flex items-center bg-secondary justify-center h-[60px] p-2 xl:h-20 xl:p-3">
              <img
                className="max-w-full max-h-full"
                src={
                  providers?.[provider][
                    theme === 'dark' ? 'iconDark' : 'iconLight'
                  ]
                }
                alt={provider}
              />
            </div>
            <div className="text-center bg-tertiary text-xs text-txt py-2 xl:text-sm">
              <p>{provider}</p>
              <p className="opacity-70">
                {selectedCategory?.providers?.[provider]} {t('games')}
              </p>
            </div>
          </div>
        </button>
      )
    },
    [categories, providers, category, selectedPlatform, theme],
  )

  if (Object.keys(selectedCategory?.providers ?? {}).length === 0) return null

  return enableSlider ? (
    <ProvidersSlider moreProviders={moreProviders}>
      {Object.keys(selectedCategory?.providers ?? {})
        .sort()
        .map(renderProvider)}
    </ProvidersSlider>
  ) : (
    <div className="mt-2 mb-2 mx-auto px-1 py-0 xl:px-[84px] xl:py-0 3xl:px-36 3xl:py-0 4xl:px-[244px] 4xl:py-0">
      <div className="flex items-center justify-between mb-3 px-1 py-0 xl:px-1.5 xl:py-0">
        <button className="text-sm text-txt relative text-left font-normal leading-[21px] mt-1 xl:text-base">
          {t('game-providers')}
        </button>
      </div>
      <div className="grid grid-cols-3 pt-1 xl:grid-cols-6">
        {Object.keys(selectedCategory?.providers ?? {})
          .sort()
          .map(renderProvider)}
      </div>
    </div>
  )
}

export default Providers
